import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://crypto-expense-management-backend.onrender.com') + '/api/v1';

export interface ReceiptUploadResponse {
  success: boolean;
  receipt_id?: number;
  s3_url?: string;
  file_key?: string;
  message: string;
  error?: string;
  // AI recommendations (not stored in DB)
  category_id?: number | null;
  policy_id?: number | null;
  category?: CategoryInReceipt | null;
  policy?: PolicyInReceipt | null;
  // OCR extracted data
  extracted_amount?: number | null;
  extracted_date?: string | null;
  extracted_vendor?: string | null;
  extracted_invoice_id?: string | null;
}

export interface CategoryInReceipt {
  id: number;
  name: string;
  description?: string;
}

export interface PolicyInReceipt {
  id: number;
  name: string;
  description?: string;
}

export interface Receipt {
  id: number;
  extracted_amount?: number;
  extracted_date?: string;
  extracted_invoice_id?: string;
  extracted_vendor?: string;
  category_id?: number;
  policy_id?: number;
  category?: CategoryInReceipt;  // Full category object
  policy?: PolicyInReceipt;      // Full policy object
  url?: string;
  ocr_doc?: string;
  file_type?: string;
  mime?: string;
  user_id: number;
  org_id: number;
  created_at: string;
  updated_at: string;
}

export interface ReceiptWithPresignedUrl extends Receipt {
  presigned_url?: string;
}

export class ReceiptService {
  private getAuthHeaders(token: string) {
    return {
      'Authorization': `Bearer ${token}`,
    };
  }

  async uploadReceipt(file: File, token: string): Promise<ReceiptUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_BASE_URL}/receipts/upload`,
        formData,
        {
          headers: {
            ...this.getAuthHeaders(token),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Receipt upload error:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Failed to upload receipt'
      );
    }
  }

  async getReceipts(token: string, skip: number = 0, limit: number = 100): Promise<Receipt[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/receipts/`,
        {
          headers: this.getAuthHeaders(token),
          params: { skip, limit },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Get receipts error:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Failed to get receipts'
      );
    }
  }

  async getReceipt(receiptId: number, token: string): Promise<ReceiptWithPresignedUrl> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/receipts/${receiptId}`,
        {
          headers: this.getAuthHeaders(token),
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Get receipt error:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Failed to get receipt'
      );
    }
  }

  async updateReceipt(
    receiptId: number, 
    updates: Partial<Receipt>, 
    token: string
  ): Promise<Receipt> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/receipts/${receiptId}`,
        updates,
        {
          headers: {
            ...this.getAuthHeaders(token),
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Update receipt error:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Failed to update receipt'
      );
    }
  }

  async deleteReceipt(receiptId: number, token: string): Promise<void> {
    try {
      await axios.delete(
        `${API_BASE_URL}/receipts/${receiptId}`,
        {
          headers: this.getAuthHeaders(token),
        }
      );
    } catch (error: any) {
      console.error('Delete receipt error:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Failed to delete receipt'
      );
    }
  }

  // Helper method to validate file type and size
  validateFile(file: File): { isValid: boolean; error?: string } {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'image/tiff',
      'image/bmp',
      'application/pdf'
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File type ${file.type} not allowed. Please upload an image or PDF file.`
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size exceeds 10MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
      };
    }

    return { isValid: true };
  }
}

export const receiptService = new ReceiptService();
